import { QueryClient, useQueryClient } from '@tanstack/react-query'
import {
  type Note,
  NoteType,
  isFolder,
  isTeamspaceNote,
  getNoteExtension,
  isCalendarNote,
  normalizeCalendarFilename,
  removeExtension,
} from '../utils/note'
import { useAuthenticatedUser } from '../providers/UserProvider'
import {
  CacheData,
  cachedNotesContext,
  getCachedData,
  getCurrentCacheVersion,
  updateCache,
} from '../contexts/CachedNotes'
import { usePrivateNotesExtension } from './usePrivateNotesExtension'
import {
  cacheKeyFromNoteType,
  cacheKeys,
  noteQueryKey,
} from '../utils/queryKeyFactory'
import * as supabase from '../lib/supabase/noteOperations'
import * as cloudkit from '../lib/cloudkit/noteOperations'
import { trackError, trackEvent } from '../lib/analytics'
import { useSafeMutation } from './useSafeMutation'
import { AuthType, AuthenticatedUser } from '../utils/User'

/**
 * Convert File[] to attachment format expected by CloudKit operations
 * Creates blob URLs and formats them as JSON-serialized attachment objects
 */
function convertFilesToAttachmentFormat(files: File[]): string[] {
  return files.map((file) => {
    const blobUrl = URL.createObjectURL(file)
    const attachment = {
      filename: file.name,
      url: blobUrl,
    }
    return JSON.stringify(attachment)
  })
}

export type CreateOptions = {
  recordName: string
  noteType: NoteType
  parent: string | null // supabase type
  filename?: string // without extension
  isDir?: boolean
  content?: string
  title?: string
  attachments?: File[] // for import operations with attachments
}

function prepareDraft(
  options: CreateOptions,
  extension: string,
  cachedNotesQueryClient: QueryClient,
  user: AuthenticatedUser
): Note {
  const {
    recordName,
    noteType,
    parent,
    filename = 'Untitled',
    // eslint-disable-next-line unicorn/prevent-abbreviations
    isDir = false,
    content = '# ',
    title = filename,
  } = options

  const correctedParent =
    // eslint-disable-next-line unicorn/no-null -- supabase needs null
    parent === 'notes' || parent === 'teamspaces' || parent === '' ? null : parent

  const baseDraft = {
    recordName,
    parent: correctedParent,
    noteType,
    isFolder: Boolean(isDir),
    filename, // Keep filename without extension
    title,
    content: isDir ? '' : content,
  }

  let processedDraft =
    user.authType === AuthType.CLOUDKIT ||
    (user.authType === AuthType.CLOUDKIT_SUPABASE && !isTeamspaceNote(noteType))
      ? handleCloudKitDraft(baseDraft, extension, cachedNotesQueryClient, user)
      : baseDraft

  // Normalize calendar filenames before final assembly
  if (isCalendarNote(noteType)) {
    const normalizedWithExtension = normalizeCalendarFilename(
      `${processedDraft.filename}.${extension}`
    )
    const withoutExtension = removeExtension(
      normalizedWithExtension,
      processedDraft.isFolder
    )
    processedDraft = { ...processedDraft, filename: withoutExtension }
  }

  // Assign filename with extension after processing
  const finalFilename = processedDraft.isFolder
    ? processedDraft.filename
    : `${processedDraft.filename}.${extension}`

  return {
    ...processedDraft,
    filename: finalFilename,
  }
}

function handleCloudKitDraft(
  draft: Note,
  extension: string,
  cachedNotesQueryClient: QueryClient,
  user: AuthenticatedUser
): Note {
  // Calendar notes shouldn't go through project note filename generation logic
  if (isCalendarNote(draft.noteType)) {
    return draft
  }

  if (!draft.recordName) {
    throw new Error('Record name is required')
  }
  // Convert just in case we had some old data
  const privateNotes = getCachedData(
    cachedNotesQueryClient,
    cacheKeys.privateProjectNotes(user.userId)
  )

  const filenameWithHierarchy = updateFilenameWithParent(
    privateNotes,
    draft.filename,
    extension,
    draft.parent
  )

  // Generate a unique base filename (without extension)
  const uniqueFilename = generateFilename(
    privateNotes,
    filenameWithHierarchy,
    draft.recordName,
    0
  )

  return {
    ...draft,
    filename: uniqueFilename, // Keep filename without extension
  }
}

function updateFilenameWithParent(
  privateNotes: Map<string, Note>,
  filename: string,
  extension: string,
  parent: string | null
): string {
  const parentFilename = parent ? privateNotes.get(parent)?.filename : undefined

  if (parentFilename) {
    // edge case: sometimes the parent filename contains the filename, so we need to remove it
    const lastSlashIndex = parentFilename.lastIndexOf('/')
    const folderPath = parentFilename.endsWith(`.${extension}`)
      ? lastSlashIndex === -1
        ? parentFilename
        : parentFilename.slice(0, lastSlashIndex)
      : parentFilename

    return `${folderPath}/${filename}`
  }

  return filename
}

function generateFilename(
  privateNotes: Map<string, Note>,
  filenameWithHierarchy: string,
  recordName: string,
  number: number
): string {
  // Construct the filename with the current number
  const newFilename =
    number > 0
      ? `${filenameWithHierarchy} ${number.toString()}`
      : filenameWithHierarchy

  // Check if this filename exists (will check later with extension)
  if (filenameExists(privateNotes, newFilename, recordName)) {
    // If it does, increment the number and try again
    return generateFilename(
      privateNotes,
      filenameWithHierarchy,
      recordName,
      number + 1
    )
  }

  return newFilename
}

function filenameExists(
  privateNotes: Map<string, Note>,
  filename: string,
  recordName: string
): boolean {
  return [...privateNotes.values()].some((note) => {
    // Remove extension from note's filename for comparison
    const noteFilename = note.isFolder
      ? note.filename
      : note.filename.slice(0, note.filename.lastIndexOf('.'))

    return noteFilename === filename && note.recordName !== recordName
  })
}

export function useCreateNote() {
  const user = useAuthenticatedUser()
  const { data: privateExtension } = usePrivateNotesExtension()

  const queryClient = useQueryClient()
  const cachedNotesQueryClient = useQueryClient({
    context: cachedNotesContext,
  })

  return useSafeMutation<Note, Error, CreateOptions, CacheData>({
    mutationFn: (options: CreateOptions) => {
      const extension = getNoteExtension(
        options.noteType,
        privateExtension,
        true
      )

      const draft = prepareDraft(
        options,
        extension,
        cachedNotesQueryClient,
        user
      )

      // Convert attachments to the format expected by CloudKit
      const attachments = options.attachments
        ? convertFilesToAttachmentFormat(options.attachments)
        : undefined

      switch (user.authType) {
        case AuthType.CLOUDKIT: {
          return cloudkit.createNote(draft, attachments)
        }
        case AuthType.SUPABASE: {
          // Supabase createNote doesn't support attachments yet
          return supabase.createNote(user.userId, draft)
        }
        case AuthType.CLOUDKIT_SUPABASE: {
          return isTeamspaceNote(options.noteType)
            ? supabase.createNote(user.teamUserId, draft)
            : cloudkit.createNote(draft, attachments)
        }
      }
    },
    onMutate: (options) => {
      // console.debug('[useCreateNote] onMutate', newNote)

      const extension = getNoteExtension(
        options.noteType,
        privateExtension,
        true
      )

      const cacheKey = cacheKeyFromNoteType(options.noteType, user)
      const previousNotes = getCachedData(cachedNotesQueryClient, cacheKey)

      const newNote = prepareDraft(
        options,
        extension,
        cachedNotesQueryClient,
        user
      )
      updateCache(cachedNotesQueryClient, [newNote], cacheKey)

      if (!isFolder(newNote)) {
        queryClient.setQueryData(noteQueryKey(newNote), () => newNote)
      }

      // return a context object with the snapshotted value
      return {
        map: previousNotes,
        version: getCurrentCacheVersion(cachedNotesQueryClient, cacheKey),
      }
    },
    onError: (createError, options, context?: CacheData) => {
      if (context) {
        const cacheKey = cacheKeyFromNoteType(options.noteType, user)
        cachedNotesQueryClient.setQueryData(cacheKey, context)
      }
      trackError(
        'useCreateNote',
        `Error creating note for ${options.recordName}`,
        {
          error: createError.message,
          noteType: options.noteType,
          filename: options.filename,
          title: options.title,
          isDir: options.isDir,
          parent: options.parent,
        }
      )
    },
    onSuccess: (newNote: Note) => {
      trackEvent(
        newNote.noteType === NoteType.TEAM_SPACE
          ? 'WEB - Teamspace Created'
          : 'WEB - Note Created',
        {
          recordName: newNote.recordName,
          noteType: NoteType[newNote.noteType],
          parent: newNote.parent,
          filename: newNote.filename,
          title: newNote.title,
        }
      )

      if (!isFolder(newNote)) {
        // set the new note in the cache
        queryClient.setQueryData(noteQueryKey(newNote), () => newNote)
      }

      const cacheKey = cacheKeyFromNoteType(newNote.noteType, user)
      updateCache(cachedNotesQueryClient, [newNote], cacheKey)
    },
  })
}
